import { useQuery } from '@tanstack/react-query'

import { endpoints, type BusinessType, type CashierSettings } from './api/endpoints'
import { t } from './strings'

/**
 * The words for the catalog by business type (ADR-0013, ADR-0026): a food outlet manages a "Menu", a shop "Produk", and
 * neither sees "layanan". Only wording changes; items, categories and prices are the same for every type.
 */
export type CatalogWords = {
  nav: string
  title: string
  tabItems: string
  add: string
  empty: string
  byItem: string
  /** The Dashboard's best sellers. */
  topItems: string
  /** Shown after a new item was saved. */
  added: string
  /** Offered next to the categories already in use when adding an item. */
  categorySuggestions: string[]
}

export function catalogWords(type: BusinessType, staff = true): CatalogWords {
  switch (type) {
    case 'food': {
      const title = staff ? t.menuAndStaff : t.menuTitle
      return {
        nav: title,
        title,
        tabItems: t.menuTitle,
        add: t.addMenu,
        empty: t.noMenu,
        byItem: t.menuTitle,
        topItems: t.topMenu,
        added: t.menuAdded,
        categorySuggestions: ['Makanan', 'Minuman', 'Camilan'],
      }
    }
    case 'retail': {
      const title = staff ? t.productsAndStaff : t.productsTitle
      return {
        nav: title,
        title,
        tabItems: t.productsTitle,
        add: t.addProduct,
        empty: t.noProducts,
        byItem: t.productsTitle,
        topItems: t.topProducts,
        added: t.productAdded,
        categorySuggestions: [],
      }
    }
    case 'other': {
      const title = staff ? t.productsServicesAndStaff : t.productsServicesTitle
      return {
        nav: title,
        title,
        tabItems: t.productsServicesTitle,
        add: t.addProductOrService,
        empty: t.noProductsServices,
        byItem: t.productsServicesTitle,
        topItems: t.topItems,
        added: t.productOrServiceAdded,
        categorySuggestions: [],
      }
    }
    default:
      return {
        nav: t.navData,
        title: t.dataTitle,
        tabItems: t.tabItems,
        add: t.add,
        empty: t.noItems,
        byItem: t.byItem,
        topItems: t.topItems,
        added: t.serviceAdded,
        categorySuggestions: [],
      }
  }
}

/** The form asks "Layanan or Produk" for each item only where both are sold. A menu or a shop sells products only. */
export const asksItemType = (type: BusinessType) => type === 'services' || type === 'other'

type FeatureSettings = Pick<
  CashierSettings,
  'business_type' | 'staff_per_line_enabled' | 'order_type_enabled' | 'table_number_enabled' | 'item_notes_enabled' | 'open_bill_enabled' | 'kitchen_ticket_enabled'
>

/**
 * Which parts each business type sees on the web (docs/plans/OWNER_WEB_BY_TYPE_PLAN.md). A part that does not belong to
 * the type stays on screen while it is on, so it can be switched off. Only the screens change: the server keeps every setting.
 */
export function featuresFor(s: FeatureSettings) {
  const type = s.business_type
  const food = type === 'food'
  const open = food || type === 'other'
  const ordersOn = s.order_type_enabled || s.table_number_enabled
  return {
    /** Staff and commission are in use. */
    staff: type === 'services' || s.staff_per_line_enabled,
    /** The switch that asks who did the work: a service business always asks, a food business or a shop is not offered it. */
    staffSwitch: type !== 'services' && (type === 'other' || s.staff_per_line_enabled),
    /** Order type, tables and item notes can be set. */
    orderSettings: open || ordersOn || s.item_notes_enabled,
    openBillSettings: open || s.open_bill_enabled,
    kitchenSettings: open || s.kitchen_ticket_enabled,
    /** Sales carry an order type or a table, so the receipt can show that line. */
    orderLine: food || ordersOn,
    /** Orders reach a kitchen, so the right to change one means something. */
    kitchen: food || s.kitchen_ticket_enabled,
  }
}

/** What the signed-in outlet sells and whether commission staff matter to it. Nothing about staff shows until it is known; if the settings cannot be read, the screens stay as for a service business. */
export function useCatalogMode() {
  const settings = useQuery({ queryKey: ['cashier-settings'], queryFn: endpoints.cashierSettings })
  const type: BusinessType = settings.data?.business_type ?? 'services'
  const staff = settings.data ? featuresFor(settings.data).staff : settings.isError
  return { type, loaded: !!settings.data || settings.isError, food: type === 'food', asksType: asksItemType(type), staff, words: catalogWords(type, staff) }
}

/** E-menu is for a food business; another type sees it only while its menu is on, so it can be switched off. */
export function useEmenuVisible(): boolean {
  const { loaded, food } = useCatalogMode()
  const settings = useQuery({ queryKey: ['emenu-settings'], queryFn: endpoints.emenuSettings, enabled: loaded && !food })
  return food || (settings.data?.enabled ?? false)
}
